/**
 * "Take me to my dashboard": the coach dashboard on TourneyNation, through
 * its sign-in page with this coach's email already filled in.
 *
 * Widget sign-in is its own embed-scoped session on another domain, so it
 * can't carry over to tourneynation.com: a coach not signed in there gets
 * one code to their email, then lands on the dashboard (a coach already
 * signed in there goes straight through). Offered only when the coach has
 * an account: a real one, or the one "Save my team" just created (or found
 * already existing for the email).
 */
import { useEffect, useState } from 'react';

import type { CoachProfile, EmbedClient } from '@/api/embed';
import type { AccountStatus } from './useAccountPromotion';

const DASHBOARD_PATH = '/coach-tn';

/** The TourneyNation site for this environment: dev widgets (on
 *  *.dev.tourneynation.com) go to dev.tourneynation.com. */
export function tourneyNationSite(host = typeof window === 'undefined' ? '' : window.location.hostname): string {
  return /(^|\.)dev\.tourneynation\.com$/.test(host) || host === 'localhost' || host === '127.0.0.1'
    ? 'https://dev.tourneynation.com'
    : 'https://tourneynation.com';
}

export function dashboardUrl(email: string): string {
  const q = new URLSearchParams({ email, return: DASHBOARD_PATH });
  return `${tourneyNationSite()}/signin?${q.toString()}`;
}

/** The dashboard link for the signed-in coach, or null when they have no
 *  TourneyNation account (a guest who unticked "Save my team").
 *  `willCreate`: Done is creating the account ("Save my team" was kept), so
 *  the button shows right away instead of popping in when that finishes;
 *  it goes only if creating the account fails. */
export function useDashboardLink(
  client: EmbedClient, promotion: AccountStatus, willCreate = false,
): string | null {
  const [me, setMe] = useState<CoachProfile | null>(null);
  useEffect(() => {
    client.getMe().then(setMe).catch(() => setMe(null));
  }, [client]);
  if (!me?.email) return null;
  const hasAccount =
    me.account_status === 'real'
    || promotion === 'created'
    || promotion === 'collision'
    || (willCreate && promotion !== 'error');
  return hasAccount ? dashboardUrl(me.email) : null;
}
